import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from "@angular/common/http"
import { inject } from "@angular/core"
import { catchError, from, switchMap, throwError } from "rxjs"
import { ClerkService } from "../services/clerk.service"

function withAuthHeader<T>(req: HttpRequest<T>, token: string | null): HttpRequest<T> {
  if (!token) return req
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  })
}

export const clerkAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const clerkService = inject(ClerkService)

  // Only add auth to API requests
  if (!req.url.includes("/api/")) {
    return next(req)
  }

  return from(clerkService.getToken()).pipe(
    switchMap((token) =>
      next(withAuthHeader(req, token)).pipe(
        catchError((err: unknown) => {
          // Defense in depth: a 401 here means the token we attached was rejected
          // (e.g. expired by the time it reached the backend). Fetch a guaranteed-fresh
          // token (bypassing Clerk's own cache) and retry exactly once before giving up --
          // this covers races the cache's normal leeway can't, without masking a genuine
          // auth failure (a second 401 still propagates as an error).
          if (err instanceof HttpErrorResponse && err.status === 401) {
            return from(clerkService.getToken({ skipCache: true })).pipe(
              switchMap((freshToken) => next(withAuthHeader(req, freshToken))),
            )
          }
          return throwError(() => err)
        }),
      ),
    ),
  )
}
